import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ChevronLeft, ChevronRight, Calendar, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';

const MONTHS_SHORT = [
  'Jan',
  'Fev',
  'Mar',
  'Abr',
  'Mai',
  'Jun',
  'Jul',
  'Ago',
  'Set',
  'Out',
  'Nov',
  'Dez',
];
const MONTHS_FULL = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
];

interface MonthYearNavigatorProps {
  month: number;
  year: number;
  showFullYear: boolean;
  onNavigate: (month: number, year: number) => void;
  onShowFullYearChange: (showFullYear: boolean, year?: number) => void;
}

export function MonthYearNavigator({
  month,
  year,
  showFullYear,
  onNavigate,
  onShowFullYearChange,
}: MonthYearNavigatorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [tempYear, setTempYear] = useState(year);
  const currentYear = new Date().getFullYear();

  useEffect(() => {
    setTempYear(year);
  }, [year]);

  const handlePrev = () => {
    if (showFullYear) {
      onNavigate(month, year - 1);
      return;
    }
    if (month === 0) onNavigate(11, year - 1);
    else onNavigate(month - 1, year);
  };

  const handleNext = () => {
    if (showFullYear) {
      onNavigate(month, year + 1);
      return;
    }
    if (month === 11) onNavigate(0, year + 1);
    else onNavigate(month + 1, year);
  };

  const handleMonthSelect = (m: number) => {
    onNavigate(m, tempYear);
    onShowFullYearChange(false);
    setIsOpen(false);
  };

  const handleFullYear = () => {
    onNavigate(month, tempYear);
    onShowFullYearChange(true, tempYear);
    setIsOpen(false);
  };

  const displayText = showFullYear ? `${year}` : `${MONTHS_FULL[month]} de ${year}`;

  return (
    <div className="flex items-center gap-1">
      <Button variant="outline" size="icon" className="h-9 w-9" onClick={handlePrev}>
        <ChevronLeft className="h-4 w-4" />
      </Button>

      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" className="min-w-[190px] justify-between gap-2 font-medium">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              {displayText}
            </div>
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[280px] p-0" align="center">
          <div className="flex items-center justify-between border-b p-2">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => setTempYear((y) => y - 1)}
              disabled={tempYear <= currentYear - 5}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-lg font-semibold">{tempYear}</span>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => setTempYear((y) => y + 1)}
              disabled={tempYear >= currentYear + 2}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>

          <div className="grid grid-cols-4 gap-1 p-2">
            {MONTHS_SHORT.map((name, i) => {
              const isSelected = !showFullYear && month === i && year === tempYear;
              const isCurrent = i === new Date().getMonth() && tempYear === currentYear;
              return (
                <Button
                  key={name}
                  variant={isSelected ? 'default' : 'ghost'}
                  size="sm"
                  className={cn(
                    'h-9',
                    isCurrent && !isSelected && 'border border-primary text-primary',
                  )}
                  onClick={() => handleMonthSelect(i)}
                >
                  {name}
                </Button>
              );
            })}
          </div>

          <div className="border-t p-2">
            <Button
              variant={showFullYear && year === tempYear ? 'default' : 'outline'}
              className="w-full"
              onClick={handleFullYear}
            >
              Ver ano inteiro ({tempYear})
            </Button>
          </div>
        </PopoverContent>
      </Popover>

      <Button variant="outline" size="icon" className="h-9 w-9" onClick={handleNext}>
        <ChevronRight className="h-4 w-4" />
      </Button>
    </div>
  );
}
